import {
  DialogContent,
  Flex,
  Icon,
  IconName,
  isNullish,
  ListItemOption,
  Loader,
  Select,
  Tabs,
  Text
} from "@theorgchartdev/storybook";
import { ComponentProps, useCallback, useEffect, useMemo } from "react";
import * as StateLib from "@/Utils/StateLib";
import {
  enumPlanSettings,
  PlanSettingsSectionNames,
  PlanSettingsSectionRoutes
} from "@/providers/NavigationProvider";
import { GetChartRefreshInfo } from "../ChartSettings/Utils";
import { UpdatePlanSettings } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";
import cx from "classnames";
import { General } from "./General";
import { PLAN_GENERAL_TEST_PREFIX } from "./Utils";
import { SettingsModalAction } from "../SettingsModal";
import { useBlocker } from "react-router-dom";
import { useToast } from "@/providers/ToastProvider";
import { isEqual } from "lodash";
import { ChartConfig, usePlanSettingsContext } from "@/providers/PlanSettingsProvider";
import { ChartTypePill } from "../ChartSettings/ChartTypePill";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import Approvals from "./Approvals";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import UpgradeTabHeader from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradeTabHeader";
import UpgradeApprovals from "./UpgradeApprovals";

interface PlanSettingsProps {
  sectionKey: keyof typeof PlanSettingsSectionRoutes;
  currentTab: number;
  setCurrentTab: (tab: number) => void;
  onCloseModal: () => void;
}

export const PlanSettings = ({
  sectionKey,
  currentTab,
  setCurrentTab,
  onCloseModal
}: PlanSettingsProps) => {
  const {
    chartConfig,
    settings,
    isLoading,
    isSaving,
    updateIsSaving,
    updateSettings,
    getClonedSettings,
    updateClonedSettings,
    launchPlanSettings
  } = usePlanSettingsContext();
  const { canAccessPlanApprovals, shouldShowPlanApprovals } = usePermissionCheck();

  const clonedSettings = getClonedSettings();
  const currentChartID = chartConfig?.ID ?? null;

  const resetData = () => {
    const clonedData = getClonedSettings();
    if (clonedData) {
      updateSettings(clonedData);
    }
  };

  const IsDirty = () => {
    if (!currentChartID || !clonedSettings || !settings) {
      return false;
    }
    return !isEqual(clonedSettings, settings);
  };

  const saveSettings = async () => {
    if (!settings || !currentChartID || isSaving) {
      return false;
    }
    try {
      updateIsSaving(true);
      const { success } = await UpdatePlanSettings(currentChartID, JSON.stringify(settings));
      if (success) {
        updateClonedSettings(settings);
        addToast({ message: appstr("Changes have been saved"), status: "success" });
        updateIsSaving(false);
        return true;
      }
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
      updateIsSaving(false);
      return false;
    } catch (err) {
      addToast({ message: appstr("An error occurred while saving"), status: "error" });
      updateIsSaving(false);
      return false;
    }
  };

  const blocker = useBlocker(IsDirty);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }

    const promptToSave = (onPostPrompt: () => void, onDismiss: () => void) => {
      const wsMgr = StateLib.GetWSMgr();
      if (!wsMgr) {
        return;
      }

      const header = appstr("Save Changes");
      const message = `${appstr("saveconfirm1")} ${appstr(getCurrentTabName())} ${appstr("saveconfirm2")}`;
      wsMgr.CreateReactAlert(
        header,
        message,
        async () => {
          const saved = await saveSettings();
          if (saved) {
            onPostPrompt();
          }
        },
        () => {
          resetData();
          onPostPrompt();
        },
        header,
        appstr("Discard"),
        onDismiss
      );
    };

    promptToSave(blocker.proceed, blocker.reset);
  }, [blocker.state]);

  const { addToast } = useToast() as unknown as {
    addToast: (toast: { message: string; status: string }) => void;
  };

  const getCurrentTabName = () => {
    const key = enumPlanSettings[currentTab as keyof typeof enumPlanSettings];
    return PlanSettingsSectionNames[key as keyof typeof PlanSettingsSectionNames];
  };

  const getChartErrorDiagnostics: (chart: Nullable<ChartConfig>) => {
    success: boolean | null;
    diagnostics: any;
  } = useCallback(chart => {
    const diagReport = { success: null, diagnostics: null };
    if (!chart) return diagReport;

    let dataSourceFailure: boolean | null = diagReport.success;
    const syncInfo = chart.sync;
    if (!syncInfo || !syncInfo.diagnostics) return diagReport;

    let targetSection = null;
    for (const aSection in syncInfo.diagnostics) {
      targetSection = syncInfo.diagnostics[aSection];
      if (Array.isArray(targetSection)) {
        const sections = targetSection.map(item => (item ? item.success : null));
        dataSourceFailure = sections.includes(false);
        if (sections.includes(null)) dataSourceFailure = null;
      } else {
        dataSourceFailure = targetSection.success === false;
      }
      if (dataSourceFailure) break;
    }

    return { success: !dataSourceFailure, diagnostics: targetSection };
  }, []);

  const isDataSourceFailure = useCallback(
    (chart: any) => getChartErrorDiagnostics(chart).success === false,
    [getChartErrorDiagnostics]
  );

  const getRefreshStatus: (
    chartConfig: Nullable<ChartConfig>,
    hasDiag: boolean | null,
    hadIssues: boolean
  ) => { icon: IconName | null; iconColor: ComponentProps<typeof Icon>["modifier"] } = (
    chartConfig,
    hasDiag,
    hadIssues
  ) => {
    if (hasDiag) {
      if (hadIssues) {
        return { icon: "circle-xmark", iconColor: "error" };
      } else if (!isNullish(chartConfig?.Timestamp)) {
        return { icon: "circle-check", iconColor: "success" };
      } else {
        return { icon: "circle-question", iconColor: "control" };
      }
    }
    return { icon: null, iconColor: undefined };
  };

  const baseChartOptions: ListItemOption[] = useMemo(() => {
    const wsMgr = StateLib.GetWSMgr();
    const allCharts = wsMgr?.fOrgInfo?.GetMasterChartList();

    if (!allCharts || !chartConfig) return [];

    return allCharts.map((chart: any) => {
      const chartName = chart.name;
      const chartHasIssue = isDataSourceFailure(chart);

      return {
        label: chartName,
        value: chart.ID,
        dataTestId: `${PLAN_GENERAL_TEST_PREFIX}base-chart-option-${chartName}`,
        icon: chartHasIssue ? "triangle-exclamation" : undefined,
        clickHandler: target => {
          const newChartID = target.value;
          if (newChartID === currentChartID) return;

          const sectionRoute = PlanSettingsSectionRoutes[sectionKey];
          launchPlanSettings(newChartID as string, sectionRoute);
        }
      } as ListItemOption;
    });
  }, [chartConfig, isDataSourceFailure, sectionKey, launchPlanSettings]);

  const getSectionConfig = (sectionName: string) => {
    switch (sectionName) {
      case PlanSettingsSectionNames.General:
        return { content: <General />, header: appstr(sectionName) as React.ReactNode };
      case PlanSettingsSectionNames.Approvals:
        return canAccessPlanApprovals
          ? { content: <Approvals />, header: appstr(sectionName) as React.ReactNode }
          : {
              content: <UpgradeApprovals />,
              header: (
                <UpgradeTabHeader
                  label={appstr(sectionName)}
                  dataTestIdPrefix={`plan-settings-${sectionName.toLowerCase()}`}
                />
              )
            };
      default:
        return { content: null, header: appstr(sectionName) as React.ReactNode };
    }
  };

  const tabs = useMemo(() => {
    return Object.entries(PlanSettingsSectionNames)
      .filter(([_, sectionName]) => {
        if (sectionName === PlanSettingsSectionNames.Approvals) {
          return shouldShowPlanApprovals;
        }
        return true;
      })
      .map(([sectionKey, sectionName]) => {
        const { content, header } = getSectionConfig(sectionName);
        return {
          header,
          dataTestId: `plan-settings-${sectionKey.toLowerCase()}-tab`,
          content: (
            <div className={cx("ocn-settings-content", "ocn-list-scrollbar", "ocn-plan-settings")}>
              {content}
            </div>
          )
        };
      });
  }, [canAccessPlanApprovals, shouldShowPlanApprovals]);

  const handleTabChange = (currentTabIndex: number) => {
    setCurrentTab(currentTabIndex);
  };

  const isPosChart = chartConfig ? IsPositionChart(chartConfig) : false;
  const diagReport = getChartErrorDiagnostics(chartConfig);
  const hasDiag = diagReport.success !== null;
  const hadIssues = hasDiag && diagReport.success === false;
  const { icon, iconColor } = getRefreshStatus(chartConfig, hasDiag, hadIssues);

  const wsMgr = StateLib.GetWSMgr();
  const dateFormat = wsMgr?.GetOrgInfo()?.GetDateFormat();
  const timeFormat = wsMgr?.GetOrgInfo()?.GetTimeFormat();

  const hasDataChanged = IsDirty();

  const saveButtonProps = {
    onClick: () => saveSettings(),
    loading: isSaving,
    disabled: !hasDataChanged || isSaving,
    dataTestId: "settings-save-btn"
  };

  const cancelButtonProps = {
    onClick: onCloseModal,
    dataTestId: "settings-cancel-btn"
  };

  return (
    <Flex direction={"column"} height={"100%"}>
      <Flex padding={"2px 32px 16px 32px"} gap={"12px"} alignItems={"flex-start"} width={"100%"}>
        <Flex width={"300px"} direction={"column"} gap={"12px"} margin={"0 0 12px 0"}>
          <Select
            dataTestId={`${PLAN_GENERAL_TEST_PREFIX}base-chart-selector`}
            options={baseChartOptions}
            listWidth={"medium"}
            selectSize={"medium"}
            optionSize={"medium"}
            label={chartConfig?.name}
            value={chartConfig?.ID}
            rightContent={<ChartTypePill isPositionDocument={isPosChart} />}
          />
          <Flex gap={"4px"} alignItems={"center"}>
            {hasDiag && icon && iconColor && (
              <Icon name={icon} size={"small"} modifier={iconColor} />
            )}
            {chartConfig && (
              <Text className="ocn-settings-text-tertiary" size={"small"}>
                {GetChartRefreshInfo(chartConfig, dateFormat, timeFormat)}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Loader.Root>
        <>
          <DialogContent customContentClass={"ocn-settings-container"}>
            <div className={"ocn-settings-content-container"}>
              <Tabs
                tabIndex={currentTab}
                customClass={"ocn-settings-tabs"}
                customContentClass={"ocn-settings-tabs-content"}
                tabs={tabs as ComponentProps<typeof Tabs>["tabs"]}
                onTabChange={handleTabChange}
              />
            </div>
          </DialogContent>
          <SettingsModalAction
            saveButtonProps={saveButtonProps}
            cancelButtonProps={cancelButtonProps}
            resetButtonProps={undefined}
            refreshButtonProps={undefined}
            forceRequeueButtonProps={undefined}
            reloadFormulasButtonProps={undefined}
          />
          {isLoading && (
            <Loader.Container>
              <Loader.Spinner />
              <Loader.Text>{appstr("Loading")}</Loader.Text>
            </Loader.Container>
          )}
        </>
      </Loader.Root>
    </Flex>
  );
};
